import {
  UserStats,
  UserTimeSegmentStat,
  WorkoutLogType,
  StatWorkoutLog,
  StatStepLog,
} from '@sworkit/api';
import {
  startOfYear,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isDate,
} from 'date-fns';
import { getDateAtMidnightFromString, getTodayString } from './utils';
import { swApi } from './app';

import { getStatsLogsDataBetweenTimestamps } from './firestore-workout-logs';
import { MemberStatsMap, WorkoutLogTotals } from './sworkit-types';
import { eachDayOfInterval } from "date-fns";

export async function getStats() {
  const now = new Date().getTime();
  const twoMonthsAgo = now - 1000 * 60 * 60 * 24 * 365 * 2;
  const yearStart = startOfYear(new Date()).getTime();
  const start = yearStart < twoMonthsAgo ? yearStart : twoMonthsAgo;
  const workoutLogs = await getStatsLogsDataBetweenTimestamps(start, now);
  const stats: UserStats = {
    today: getTodayStats(workoutLogs),
    week: getWeekStats(workoutLogs),
    lastWeek: getLastWeekStats(workoutLogs),
    month: getMonthStats(workoutLogs),
    lastMonth: getLastMonthStats(workoutLogs),
    year: getYearStats(workoutLogs),
    yearMonthStats: getThisYearStatsByMonth(workoutLogs),
  };
  const statsWithNames = await addWorkoutNamesToStats(stats);
  return statsWithNames;
}

async function addWorkoutNamesToStats(stats: UserStats) {
  try {
    const promises = Object.keys(stats).map(async (key) => {
      if (stats[key] && stats[key].mostPopularWorkoutSlug) {
        stats[key].mostPopularWorkoutName = await getWorkoutName(
          stats[key].mostPopularWorkoutSlug
        );
      }
    });
    await Promise.all(promises);
  } catch (err) {
    console.error('error adding workout name to stats', err);
  }
  return stats;
}

export async function getYearlyStats(year: number) {
  const start = startOfYear(
    getDateAtMidnightFromString(`${year}-01-01`)
  ).getTime();
  const end = endOfYear(start).getTime();
  const workoutLogs = await getStatsLogsDataBetweenTimestamps(start, end);
  const yearlyStats = getWorkoutLogStatsBetweenDates(workoutLogs, start, end);
  yearlyStats.mostPopularWorkoutName = await getWorkoutName(
    yearlyStats.mostPopularWorkoutSlug
  );
  return yearlyStats;
}

async function getWorkoutName(slug: string) {
  const workout = swApi.workouts.get(slug);
  return workout.name;
}

function getTodayStats(workoutLogs: StatWorkoutLog[]) {
  const today = startOfDay(new Date()).getTime();
  const dayEnd = endOfDay(today).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, today, dayEnd);
}

function getWeekStats(workoutLogs: StatWorkoutLog[]) {
  const weekStart = startOfWeek(new Date()).getTime();
  const weekEnd = endOfWeek(weekStart).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, weekStart, weekEnd);
}

function getLastWeekStats(workoutLogs: StatWorkoutLog[]) {
  const weekStart = subWeeks(startOfWeek(new Date()), 1).getTime();
  const weekEnd = endOfWeek(weekStart).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, weekStart, weekEnd);
}

function getMonthStats(workoutLogs: StatWorkoutLog[]) {
  const monthStart = startOfMonth(new Date()).getTime();
  const monthEnd = endOfMonth(monthStart).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, monthStart, monthEnd);
}

function getLastMonthStats(workoutLogs: StatWorkoutLog[]) {
  const monthStart = subMonths(startOfMonth(new Date()), 1).getTime();
  const monthEnd = endOfMonth(monthStart).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, monthStart, monthEnd);
}

function getYearStats(workoutLogs: StatWorkoutLog[]) {
  const yearStart = startOfYear(new Date()).getTime();
  const yearEnd = endOfYear(yearStart).getTime();
  return getWorkoutLogStatsBetweenDates(workoutLogs, yearStart, yearEnd);
}

function getWorkoutLogStatsBetweenDates(
  workoutLogs: StatWorkoutLog[],
  startDate: number,
  endDate: number
) {
  let stats: UserTimeSegmentStat = {
    averageDuration: 0,
    totalSessions: 0,
    workoutTime: 0,
    caloriesBurned: 0,
    goldMedals: 0,
    silverMedals: 0,
    bronzeMedals: 0,
    activeDays: 0,
  };
  if (!workoutLogs || !workoutLogs.length) return stats;
  const activeDaysArray: string[] = [];
  const workoutCountByType: { [key in WorkoutLogType]: number } = {
    strength: 0,
    cardio: 0,
    yoga: 0,
    stretching: 0,
    'external-activity': 0,
    custom: 0,
    plan: 0,
    meditation: 0,
    studio: 0,
  };
  const workoutCountByWorkoutSlug: { [slug: string]: number } = {};
  workoutLogs.map((workoutLog) => {
    if (isWorkoutLogBetweenTimestamps(workoutLog, startDate, endDate)) {
      stats.workoutTime += workoutLog.minutesCompleted;
      stats.totalSessions += 1;
      stats.caloriesBurned += workoutLog.caloriesBurned;
      if (workoutLog.slug) {
        if (!workoutCountByWorkoutSlug[workoutLog.slug]) {
          workoutCountByWorkoutSlug[workoutLog.slug] = 0;
        }
        workoutCountByWorkoutSlug[workoutLog.slug] =
          workoutCountByWorkoutSlug[workoutLog.slug] + 1;
      }
      if (workoutLog.type) {
        workoutCountByType[workoutLog.type]++;
      }
      if (workoutLog.minutesCompleted >= 30) {
        stats.goldMedals++;
      } else if (
        workoutLog.minutesCompleted >= 15 &&
        workoutLog.minutesCompleted < 30
      ) {
        stats.silverMedals++;
      } else if (
        workoutLog.minutesCompleted >= 5 &&
        workoutLog.minutesCompleted < 15
      ) {
        stats.bronzeMedals++;
      }
      const day = format(new Date(workoutLog.timestampMillis), 'yyyy-MM-dd');
      activeDaysArray.push(day);
    }
  });
  if (stats.totalSessions > 0) {
    const uniqueActiveDays = activeDaysArray.filter(
      (date, index, array) => array.indexOf(date) === index
    );
    stats.activeDays = uniqueActiveDays.length;
    stats.averageDuration = _roundNumber(
      stats.workoutTime / stats.totalSessions,
      0
    );
  } else {
    console.debug('You have no workouts between', startDate, endDate);
  }

  Object.keys(workoutCountByWorkoutSlug).map((slug) => {
    const workoutSlugCount = workoutCountByWorkoutSlug[slug];
    const currentWinner = stats.mostPopularWorkoutSlug;
    if (
      !currentWinner ||
      workoutSlugCount > workoutCountByWorkoutSlug[currentWinner]
    ) {
      stats.mostPopularWorkoutSlug = slug;
    }
  });
  (Object.keys(workoutCountByType) as WorkoutLogType[]).map((categorySlug) => {
    const workoutCategoryCount = workoutCountByType[categorySlug];
    const currentWinner = stats.mostPopularType;
    if (
      !currentWinner ||
      workoutCategoryCount > workoutCountByType[currentWinner]
    ) {
      stats.mostPopularType = categorySlug;
    }
  });
  return stats;
}

function getThisYearStatsByMonth(workoutLogs: StatWorkoutLog[]) {
  const thisYear = new Date().getFullYear();
  let monthStats = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(() => {
    return {
      averageDuration: 0,
      totalSessions: 0,
      workoutTime: 0,
      caloriesBurned: 0,
      goldMedals: 0,
      silverMedals: 0,
      bronzeMedals: 0,
      activeDays: 0,
    };
  });
  const activeDaysArray: string[] = [];
  if (workoutLogs && workoutLogs.length > 0) {
    const workoutLogsThisYear = workoutLogs.filter(
      (workoutLog) =>
        new Date(workoutLog.timestampMillis).getFullYear() === thisYear
    );

    workoutLogsThisYear.map((workoutLog) => {
      const date = new Date(workoutLog.timestampMillis);
      const month = date.getMonth();
      monthStats[month].totalSessions += 1;
      monthStats[month].caloriesBurned += workoutLog.caloriesBurned;
      monthStats[month].workoutTime += workoutLog.minutesCompleted;
      if (workoutLog.minutesCompleted >= 30) {
        monthStats[month].goldMedals++;
      } else if (
        workoutLog.minutesCompleted >= 15 &&
        workoutLog.minutesCompleted < 30
      ) {
        monthStats[month].silverMedals++;
      } else if (
        workoutLog.minutesCompleted >= 5 &&
        workoutLog.minutesCompleted < 15
      ) {
        monthStats[month].bronzeMedals++;
      }
      const day = format(date, 'yyyy-MM-dd');
      if (activeDaysArray.indexOf(day) == -1) {
        monthStats[month].activeDays += 1;
        activeDaysArray.push(day);
      }
    });
    monthStats = monthStats.map((month) => {
      if (!month.totalSessions) return month;
      month.averageDuration = _roundNumber(
        month.workoutTime / month.totalSessions,
        0
      );
      return month;
    });
  } else {
    console.debug('You have no workouts in your log');
  }
  return monthStats;
}

function _roundNumber(num: any, decimals: any) {
  const roundedNumber = Number(num.toFixed(decimals));
  return roundedNumber;
}

function isWorkoutLogBetweenTimestamps(
  workoutLog: StatWorkoutLog,
  startTimestamp: number,
  endTimestamp: number
) {
  return (
    startTimestamp < workoutLog.timestampMillis &&
    workoutLog.timestampMillis < endTimestamp
  );
}

export function getStatsBetweenDates(stats: MemberStatsMap, startDate: Date, endDate: Date) {
  return { ...getStepLogTotalsBetweenDates(stats, startDate, endDate), ...getUpdatedWorkoutLogStatsBetweenDates(stats, startDate, endDate) }
}

//todo: include everything we need to replace userStats
export const getUpdatedWorkoutLogStatsBetweenDates = (stats: MemberStatsMap, startDate: Date, endDate: Date) => {
  const startMillis = startDate.valueOf()
  const endMillis = endDate.valueOf() + 24 * 60 * 60 * 1000  //make sure it's at the end of the day
  const allWorkoutLogs = Object.values(stats || {}).map(stat => Object.values(stat?.workoutLogs || {})).flatMap(w => w)
  const filteredWorkouts = allWorkoutLogs?.filter(log => isWorkoutLogBetweenTimestamps(log as StatWorkoutLog, startMillis, endMillis))
  const workoutLogTotals = filteredWorkouts.reduce((acc: WorkoutLogTotals, cur: StatWorkoutLog) => {
    return {
      workoutCount: acc.workoutCount + 1,
      workoutMinutes: acc.workoutMinutes + cur.minutesCompleted,
    };
  }, { workoutCount: 0, workoutMinutes: 0 });
  return workoutLogTotals
}

export const getStepLogTotalsBetweenDates = (stats: MemberStatsMap, startDate: Date, endDate: Date) => {
  const defaultTotals = { stepCount: 0, distanceMeters: 0, activeStepDays: 0, dailyAverageSteps: 0 };
  if (!isDate(startDate) || !isDate(endDate) || isBefore(endDate, startDate)) {
    return defaultTotals
  }
  const days = eachDayOfInterval({
    start: startDate,
    end: endDate
  }).map(d => format(d, 'yyyy-MM-dd')) || []
  const stepLogTotals = days.reduce((acc, day: string) => {
    const yearMonth = day.slice(0, 7)
    const cur = stats?.[yearMonth]?.stepLogs?.[day] as StatStepLog || defaultTotals
    return {
      stepCount: acc.stepCount + cur.stepCount,
      distanceMeters: acc.distanceMeters + cur.distanceMeters,
      activeStepDays: cur.stepCount ? acc.activeStepDays + 1 : acc.activeStepDays
    };
  }, defaultTotals);
  const today = getTodayString()
  const todayStats = stats?.[today.slice(0, 7)]?.stepLogs?.[today] as StatStepLog || defaultTotals as StatStepLog
  const dailyAverageSteps = getDailyAverageSteps(todayStats, startDate, endDate, stepLogTotals)
  return { ...stepLogTotals, dailyAverageSteps };
}

export const getDailyAverageSteps = (todayStats: StatStepLog, startDate: Date, endDate: Date, totals) => {
  if (!totals?.activeStepDays) return 0
  const runningAverage = Math.round((totals?.stepCount || 0) / (totals?.activeStepDays)) || 0
  if (isBefore(new Date(), startDate) || isAfter(new Date(), endDate) || !todayStats?.stepCount) {
    return runningAverage
  }
  const totalWithoutToday = (totals?.stepCount || 0) - (todayStats?.stepCount || 0)
  const averageBeforeToday = Math.round((totalWithoutToday / (totals?.activeStepDays - 1))) || 0
  if (runningAverage >= averageBeforeToday) {
    return runningAverage
  }
  return averageBeforeToday;
} 
